/**
 * VipVanTour 시나리오 챗봇 — 정본 데이터 v2.1 (D1)
 *
 * 출처: planning/chatbot/07_최종_통합_시나리오.md (97 노드)
 * 코드 매핑: 1:1
 *
 * - 1차 릴리스: 프론트엔드 상수 (이 파일)
 * - 2차 이후: 백엔드 API(/api/chatbot/scenarios) + 어드민 CRUD 이관 예정
 *
 * ⚠️  이 챗봇은 시나리오 기반(규칙 기반 의사결정 트리)입니다.
 *     LLM 자연어 AI 챗봇이 아닙니다.
 *
 * ============================================================
 * 노드 타입 (8종)
 * ============================================================
 *
 *  menu       카테고리 그리드 (메인 메뉴 등)
 *  choice     봇 메시지 + 수직 선택지 리스트
 *  bot        봇 메시지만 (자동 next 또는 답변 후 피드백)
 *  input      자유 텍스트 입력 + 정규식 검증 + 3-strike 폴백
 *  lookup     백엔드 API 호출 + 로딩 + 분기
 *  modal      overlay 모달 (이탈방지·confirm)
 *  prompt     panel 내부 토스트 (idle 알림)
 *  action     단독 노드 X — button.action: navigate|link|tel
 *
 * ============================================================
 * 노드 ID 명명 규칙
 * ============================================================
 *
 *  p_<category>_<sub> 형식
 *  - p_welcome, p_main_menu (Phase 1)
 *  - p_vehicle_*, p_coverage_*, p_booking_*, p_res_* (Phase 2)
 *  - p_meeting_*, p_loc_share, p_faq_*, p_pay_*, p_consultant (Phase 2)
 *  - p_feedback_*, p_idle_prompt, p_exit_guard_*, p_close_* (Phase 3)
 *
 * ============================================================
 * i18n 키 네임스페이스
 * ============================================================
 *
 *  chatbot.<category>.<sub> 형식
 *  D2 단계에서 src/i18n/locales/ko/chatbot.json 생성
 */

/* =============== Type Constants =============== */

export const NODE_TYPES = Object.freeze({
  MENU:          'menu',
  CHOICE:        'choice',
  BOT:           'bot',
  INPUT:         'input',
  LOOKUP:        'lookup',
  MODAL:         'modal',
  PROMPT:        'prompt',
  IMAGE_GALLERY: 'image_gallery',
});

export const ACTION_TYPES = Object.freeze({
  NAVIGATE: 'navigate',
  LINK:     'link',
  TEL:      'tel',
});

export const SOURCES = Object.freeze({
  XMIND: 'xmind',
  TECH:  'tech',
  MIXED: 'mixed',
  WARN:  'warn',
});

const VALID_NODE_TYPES = Object.values(NODE_TYPES);

export function validateNode(node) {
  if (!node || typeof node !== 'object') return ['node must be an object'];
  const errors = [];
  if (!node.type) errors.push('missing type');
  else if (!VALID_NODE_TYPES.includes(node.type)) errors.push(`invalid type: ${node.type}`);
  if (node.type === NODE_TYPES.INPUT) {
    if (!node.validate) errors.push('input node missing validate');
    if (!node.next) errors.push('input node missing next');
    if (!node.fallback) errors.push('input node missing fallback');
  }
  if (node.type === NODE_TYPES.LOOKUP) {
    if (!node.api) errors.push('lookup node missing api');
    if (!node.onSuccess) errors.push('lookup node missing onSuccess');
    if (!node.onError) errors.push('lookup node missing onError');
  }
  return errors;
}

/* =============== Channels & Routes =============== */

const CHANNELS = {
  kakao:    'https://pf.kakao.com/_JScZX/friend',
  whatsapp: 'https://wa.me/821022500365',
  line:     'https://line.me/R/ti/p/@690ljwok',
  email:    'vipvantour.korea@gmail.com',
  phone:    '010-2250-0365',
  inquiry:  '/inquiry',
  shopping: 'https://vipvantour.com/shopping',
  // BugHerd #24: hyundaiCatalog 외부 링크 제거 (inline 이미지 갤러리로 대체).
};

/* =============== Scenario Data =============== */

export const CHATBOT_SCENARIOS = {
  version: '2.1',
  start: 'p_main_menu',

  meta: {
    chatbotName: 'VIP 컨시어지',
    tone: 'formal_polite',
    businessHours: { startHour: 9, endHour: 18, weekdays: [1, 2, 3, 4, 5], whatsapp24h: true },
    channels: CHANNELS,
  },

  nodes: {
    /* ============================================================
     * Phase 1 — 진입
     * ============================================================ */

    p_welcome: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.welcome.greeting',
      next: 'p_main_menu',
    },

    p_main_menu: {
      type: NODE_TYPES.MENU,
      source: SOURCES.XMIND,
      // #22: 'chatbot.welcome.greeting' 끝 문장이 이미 "무엇을 도와드릴까요?" 라
      //      subMessageKey 의 'chatbot.welcome.question' 과 중복되던 이슈 → subMessageKey 제거.
      messageKey: 'chatbot.welcome.greeting',
      items: [
        { icon: 'van',         labelKey: 'chatbot.main_menu.vehicle',     next: 'p_vehicle_intro' },
        { icon: 'place',       labelKey: 'chatbot.main_menu.coverage',    next: 'p_coverage_intro' },
        { icon: 'calendar',    labelKey: 'chatbot.main_menu.booking',     next: 'p_booking_intro' },
        { icon: 'reservation', labelKey: 'chatbot.main_menu.reservation', next: 'p_res_intro' },
        { icon: 'place',       labelKey: 'chatbot.main_menu.meeting',     next: 'p_meeting_intro' },
        { icon: 'faq',         labelKey: 'chatbot.main_menu.faq',         next: 'p_faq_intro' },
        { icon: 'headset',     labelKey: 'chatbot.main_menu.consultant',  next: 'p_consultant', full: true },
      ],
    },

    /* ============================================================
     * Phase 2 — 🚌 차량 안내
     * ============================================================ */

    p_vehicle_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.intro',
      choices: [
        { icon: 'van',     labelKey: 'chatbot.vehicle.specs',       next: 'p_vehicle_specs' },
        { icon: 'info',    labelKey: 'chatbot.vehicle.number',      next: 'p_vehicle_number' },
        { icon: 'person',  labelKey: 'chatbot.vehicle.capacity',    next: 'p_vehicle_capacity' },
        { icon: 'bag',     labelKey: 'chatbot.vehicle.luggage',     next: 'p_vehicle_luggage_intro' },
        { icon: 'info',    labelKey: 'chatbot.vehicle.carseat',     next: 'p_vehicle_carseat_intro' },
        { icon: 'pet',     labelKey: 'chatbot.vehicle.pet',         next: 'p_vehicle_pet' },
      ],
    },

    p_vehicle_specs: {
      // BugHerd #24: Depth 최소화 — 기존 specs_info(외관/안전 분기) 단계 제거.
      // 명칭 변경: "차량 정보" → "차량 외부 및 안전" / "내부 편의 시설" → "차량 내부 편의 시설"
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.specs_msg',
      choices: [
        { labelKey: 'chatbot.vehicle.specs_info',     next: 'p_vehicle_specs_exterior_safety' },
        { labelKey: 'chatbot.vehicle.specs_interior', next: 'p_vehicle_specs_interior' },
      ],
    },

    /* BugHerd #24: 외부 카탈로그 링크 → inline 이미지 갤러리로 대체.
       이미지 자산: frontend/public/images/chatbot/ (현대 공식 이미지) */
    p_vehicle_specs_exterior_safety: {
      type: NODE_TYPES.IMAGE_GALLERY,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.specs_safety_msg',
      images: [
        { src: '/images/chatbot/staria_lounge_mobility_gallery_exterior_front_side_m.jpg',
          captionKey: 'chatbot.vehicle.gallery.exterior_front_side' },
        { src: '/images/chatbot/staria_lounge_mobility_gallery_exterior_side_open_m.jpg',
          captionKey: 'chatbot.vehicle.gallery.exterior_side_open' },
        { src: '/images/chatbot/staria_lounge_mobility_design_full_led_headlamp_off_m.jpg',
          captionKey: 'chatbot.vehicle.gallery.led_headlamp' },
        { src: '/images/chatbot/staria_lounge_mobility_safety_7air_bag_m.jpg',
          captionKey: 'chatbot.vehicle.gallery.airbag_7' },
        { src: '/images/chatbot/staria_lounge_mobility_safety_multi_collision_prevention_automatic_braking_system.jpg',
          captionKey: 'chatbot.vehicle.gallery.collision_braking' },
        { src: '/images/chatbot/staria_lounge_mobility_features_lateral_parking_distance_warning_m.jpg',
          captionKey: 'chatbot.vehicle.gallery.lateral_warning' },
      ],
    },

    p_vehicle_specs_interior: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.specs_interior_msg',
    },

    p_vehicle_number: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.number_msg',
      // ⚠️ 5월 차량번호 데이터 수령 시 활성화
      choices: [
        { icon: 'reservation', labelKey: 'chatbot.vehicle.number_check', action: ACTION_TYPES.NAVIGATE, to: '/reservation' },
      ],
    },

    p_vehicle_capacity: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.capacity_msg',
      // BugHerd #105: 좌석 배치도 이미지 (스타리아 라운지 인테리어)
      imageUrl: '/images/chatbot/staria-lounge-seat-layout.png',
    },

    p_vehicle_luggage_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_intro',
      choices: [
        { labelKey: 'chatbot.vehicle.luggage_carrier',    next: 'p_vehicle_luggage_carrier' },
        { labelKey: 'chatbot.vehicle.luggage_golf',       next: 'p_vehicle_luggage_golf' },
        { labelKey: 'chatbot.vehicle.luggage_ski',        next: 'p_vehicle_luggage_ski' },
        { labelKey: 'chatbot.vehicle.luggage_wheelchair', next: 'p_vehicle_luggage_wheelchair' },
        { labelKey: 'chatbot.vehicle.luggage_other',      next: 'p_vehicle_luggage_other' },
      ],
    },

    p_vehicle_luggage_carrier: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_carrier_msg',
      choices: [{ labelKey: 'chatbot.common.luggage_inquiry', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry }],
    },

    p_vehicle_luggage_golf: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_golf_msg',
      choices: [{ labelKey: 'chatbot.common.luggage_inquiry', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry }],
    },

    p_vehicle_luggage_ski: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_ski_msg',
      choices: [{ labelKey: 'chatbot.common.luggage_inquiry', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry }],
    },

    p_vehicle_luggage_wheelchair: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_wheelchair_msg',
      choices: [{ labelKey: 'chatbot.common.luggage_inquiry', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry }],
    },

    p_vehicle_luggage_other: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.luggage_other_msg',
      choices: [{ labelKey: 'chatbot.common.luggage_inquiry', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry }],
    },

    p_vehicle_carseat_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.carseat_intro',
      choices: [
        { labelKey: 'chatbot.vehicle.carseat_law',     next: 'p_vehicle_carseat_law' },
        { labelKey: 'chatbot.vehicle.carseat_pricing', next: 'p_consultant' },
      ],
      // ⚠️ 카시트 추가 요금 액수 회신 필요
    },

    p_vehicle_carseat_law: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.carseat_law_msg',
      choices: [
        { labelKey: 'chatbot.vehicle.carseat_baby',   next: 'p_vehicle_carseat_baby' },
        { labelKey: 'chatbot.vehicle.carseat_junior', next: 'p_vehicle_carseat_junior' },
      ],
    },

    p_vehicle_carseat_baby: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.carseat_baby_msg',
      // BugHerd #103: 영아용 카시트 모델 이미지 (cybex Sirona T)
      imageUrl: '/images/chatbot/cybex-sirona-t.jpg',
    },

    p_vehicle_carseat_junior: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.carseat_junior_msg',
      // BugHerd #104: 주니어 카시트 모델 이미지 2개 (Cybex Solution M-Fix SL + Recaro Monza Nova Evo)
      imageUrls: [
        '/images/chatbot/cybex-solution-m-fix-sl.jpg',
        '/images/chatbot/recaro-monza-nova-evo.jpg',
      ],
    },

    p_vehicle_pet: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.vehicle.pet_msg',
      choices: [
        { icon: 'pet', labelKey: 'chatbot.vehicle.pet_link', action: ACTION_TYPES.NAVIGATE, to: '/transfer/pet' },
      ],
    },

    /* ============================================================
     * Phase 2 — 📍 이용 가능 지역
     * ============================================================ */

    p_coverage_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.intro',
      choices: [
        { icon: 'airport',  labelKey: 'chatbot.coverage.airport',  next: 'p_coverage_airport' },
        { icon: 'golf',     labelKey: 'chatbot.coverage.golf',     next: 'p_coverage_golf' },
        { icon: 'ski',      labelKey: 'chatbot.coverage.ski',      next: 'p_coverage_ski' },
        { icon: 'hospital', labelKey: 'chatbot.coverage.hospital', next: 'p_coverage_hospital' },
        { icon: 'pet',      labelKey: 'chatbot.coverage.pet',      next: 'p_coverage_pet' },
        { icon: 'business', labelKey: 'chatbot.coverage.business', next: 'p_coverage_business' },
      ],
    },

    // BugHerd #26: 챗봇 안 단계 줄이고 메인 페이지로 직접 이동.
    // book_now 클릭 시 p_booking_intro(private/tour/shopping 분기)를 또 거치지 않도록
    // 각 서비스 페이지로 직접 NAVIGATE.
    p_coverage_airport: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.airport_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/airport' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_coverage_golf: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.golf_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/golf' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_coverage_ski: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.ski_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/resort' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_coverage_hospital: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.hospital_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/hospital' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_coverage_pet: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.pet_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/pet' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_coverage_business: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.coverage.business_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/business' },
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    /* ============================================================
     * Phase 2 — 📅 실시간 예약·요금
     * ============================================================ */

    p_booking_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.booking.intro',
      choices: [
        { icon: 'van',      labelKey: 'chatbot.booking.private',  next: 'p_booking_private' },
        { icon: 'place',    labelKey: 'chatbot.booking.tour',     next: 'p_booking_tour' },
        { icon: 'shopping', labelKey: 'chatbot.booking.shopping', next: 'p_booking_shopping' },
      ],
    },

    p_booking_private: {
      // BugHerd #26: 챗봇 내부 입력 단계 전부 제거 → 메인 페이지로 직접 navigate.
      // 공항도 방향 선택 단계 제거 (페이지 자체에 from-airport/to-airport 탭이 있어 챗봇에서 미리 받을 필요 없음).
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.booking.private_msg',
      choices: [
        { icon: 'airport',  labelKey: 'chatbot.coverage.airport',  action: ACTION_TYPES.NAVIGATE, to: '/transfer/airport' },
        { icon: 'golf',     labelKey: 'chatbot.coverage.golf',     action: ACTION_TYPES.NAVIGATE, to: '/transfer/golf' },
        { icon: 'ski',      labelKey: 'chatbot.coverage.ski',      action: ACTION_TYPES.NAVIGATE, to: '/transfer/resort' },
        { icon: 'hospital', labelKey: 'chatbot.coverage.hospital', action: ACTION_TYPES.NAVIGATE, to: '/transfer/hospital' },
        { icon: 'pet',      labelKey: 'chatbot.coverage.pet',      action: ACTION_TYPES.NAVIGATE, to: '/transfer/pet' },
        { icon: 'business', labelKey: 'chatbot.coverage.business', action: ACTION_TYPES.NAVIGATE, to: '/transfer/business' },
      ],
    },

    /* BugHerd #26 정리: golf/ski/pet/business 의 챗봇 내부 INPUT 단계 + 종착 안내 노드 제거.
       p_booking_private 분기 6개 모두 NAVIGATE 직결로 전환되며 dead 된 결과.
       제거된 노드: p_booking_private_{golf,ski,pet,business},
                   p_booking_golf_{origin,search,dest}, p_booking_ski_{origin,select,dest},
                   p_booking_done_{golf,ski,pet,business} */

    p_booking_tour: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.booking.tour_msg',
      choices: [
        { icon: 'place', labelKey: 'chatbot.booking.tour_link', action: ACTION_TYPES.NAVIGATE, to: '/tour' },
      ],
    },

    p_booking_shopping: {
      // BugHerd #27: 4개 카테고리 버튼 → 단일 "프라이빗 쇼핑 상품 구경가기" 버튼으로 통합.
      // 2026-05-07 코멘트: 새 창(LINK)으로 열려 모바일 환경에서 탭 관리 부담 + 이탈 → 내부 라우팅 NAVIGATE 로.
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.booking.shopping_msg',
      choices: [
        { icon: 'shopping', labelKey: 'chatbot.booking.shopping_all', action: ACTION_TYPES.NAVIGATE, to: '/shopping' },
      ],
    },

    /* ============================================================
     * Phase 2 — 🔎 예약 조회·변경·취소
     * ============================================================ */

    p_res_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,        // 🔧 cancel/refund_policy 추가로 MIXED
      messageKey: 'chatbot.res.intro',
      choices: [
        { labelKey: 'chatbot.res.view',           next: 'p_res_view_input' },
        { labelKey: 'chatbot.res.change',         next: 'p_res_change_info' },
        { labelKey: 'chatbot.res.cancel',         next: 'p_res_cancel_input' },
        { labelKey: 'chatbot.res.noshow_label',   next: 'p_res_noshow' },
        { labelKey: 'chatbot.res.cancel_policy',  next: 'p_res_cancel_policy' },  // 🔧 신규
        { labelKey: 'chatbot.res.refund_policy',  next: 'p_res_refund_policy' },  // 🔧 신규
      ],
    },

    /* BugHerd #66: 예약번호(VVT-…) 식별성 문제 → 비회원 예약 조회처럼 이메일 입력 방식.
       이메일 1건당 여러 예약 매칭 가능하나 useReservationLookup 이 created_at desc 첫 번째 사용. */
    p_res_view_input: {
      type: NODE_TYPES.INPUT,
      source: SOURCES.TECH,
      messageKey: 'chatbot.res.view_input_prompt',
      placeholder: 'name@example.com',
      validate: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$',
      errorMessageKey: 'chatbot.res.view_input_error',
      maxRetries: 3,
      fallback: 'p_consultant',
      next: 'p_res_view_lookup',
    },

    p_res_view_lookup: {
      type: NODE_TYPES.LOOKUP,
      source: SOURCES.TECH,
      messageKey: 'chatbot.res.view_loading',
      api: { method: 'GET', endpoint: '/api/reservations/lookup?email={id}' },
      onSuccess: 'p_res_view_result',
      onError: 'p_res_not_found',
    },

    p_res_view_result: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.res.view_result',  // 동적 변수 치환은 D4에서 구현
      choices: [
        { labelKey: 'chatbot.res.change',  next: 'p_res_change_info' },
        { labelKey: 'chatbot.res.cancel',  next: 'p_res_cancel_lookup' },
      ],
    },

    p_res_not_found: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.res.not_found',
      choices: [
        { labelKey: 'chatbot.common.retry_input', next: 'p_res_view_input' },
        { labelKey: 'chatbot.common.consultant',   next: 'p_consultant' },
      ],
    },

    p_res_change_info: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.res.change_info',
      choices: [
        // BugHerd #26: "실시간 예약 가능 여부 확인" → p_booking_intro(private/tour/shopping 분기)
        // 거치지 않고 6개 서비스 NAVIGATE 카드로 바로. private 노드 자체가 NAVIGATE-only 라
        // 사용자가 어떤 서비스로 변경할지 한 번에 골라서 메인 페이지로 이동.
        { labelKey: 'chatbot.res.change_check_realtime', next: 'p_booking_private' },
        { labelKey: 'chatbot.res.change_proceed_cancel', next: 'p_res_cancel_input' },
        { labelKey: 'chatbot.common.consultant',           next: 'p_consultant' },
      ],
    },

    p_res_cancel_input: {
      type: NODE_TYPES.INPUT,
      source: SOURCES.TECH,
      messageKey: 'chatbot.res.cancel_input_prompt',
      placeholder: 'name@example.com',
      validate: '^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$',
      errorMessageKey: 'chatbot.res.view_input_error',
      maxRetries: 3,
      fallback: 'p_consultant',
      next: 'p_res_cancel_lookup',
    },

    p_res_cancel_lookup: {
      type: NODE_TYPES.LOOKUP,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.res.cancel_loading',
      api: { method: 'GET', endpoint: '/api/reservations/lookup?email={id}' },
      onSuccess: 'p_res_cancel_full',  // D4에서 출발 시각 비교로 동적 분기
      onError: 'p_res_not_found',
    },

    p_res_cancel_full: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.res.cancel_full',
      choices: [
        { labelKey: 'chatbot.res.cancel_proceed', action: ACTION_TYPES.NAVIGATE, to: '/reservation' },
        { labelKey: 'chatbot.common.consultant',   next: 'p_consultant' },
      ],
    },

    p_res_cancel_blocked: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.res.cancel_blocked',
      choices: [
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    p_res_noshow: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.res.noshow_msg',
      choices: [
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
      ],
    },

    /* ⚠️ 테크프리 가공 (deviation from client) — 2026-04-29
     * 클라이언트 XMind 원본: 취소/환불 규정이 [예약 변경]·[예약 취소] 분기 안에만 존재.
     * 변경: 독립 메뉴 [취소 규정] · [환불 규정] 신설 — 예약 메뉴와 FAQ 양쪽에서 진입 가능.
     * 사유: 클라가 보내준 시나리오에서 취소·환불 정책 찾기가 어렵다는 사용자 피드백.
     *       내용 자체는 XMind 정의(24h 100%/0% 룰) + 결제 안내(PortOne 3-7 영업일) 통합. */
    p_res_cancel_policy: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.res.cancel_policy_msg',
      choices: [
        { labelKey: 'chatbot.res.cancel',           next: 'p_res_cancel_input' },
        { labelKey: 'chatbot.res.refund_policy',    next: 'p_res_refund_policy' },
        { labelKey: 'chatbot.common.consultant',    next: 'p_consultant' },
      ],
    },

    p_res_refund_policy: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.res.refund_policy_msg',
      choices: [
        { labelKey: 'chatbot.res.cancel',           next: 'p_res_cancel_input' },
        { labelKey: 'chatbot.res.cancel_policy',    next: 'p_res_cancel_policy' },
        { labelKey: 'chatbot.common.consultant',    next: 'p_consultant' },
      ],
    },

    /* ============================================================
     * Phase 2 — 🚩 미팅 포인트
     * ============================================================ */

    p_meeting_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.intro',
      choices: [
        { icon: 'airport', labelKey: 'chatbot.meeting.airport',  next: 'p_meeting_airport_intro' },
        { icon: 'place',   labelKey: 'chatbot.meeting.hotel',    next: 'p_meeting_hotel' },
        { icon: 'info',    labelKey: 'chatbot.meeting.lost',     next: 'p_meeting_lost' },
      ],
    },

    p_meeting_airport_intro: {
      // BugHerd #28: yes/no 선택 후 안내+Q 선택 화면(intro)으로 분기.
      // 기존엔 바로 Q1 답변으로 갔지만, 클라 의도(Q1/Q2/Q3 선택형)에 맞춰 한 단계 추가.
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_intro',
      choices: [
        { labelKey: 'chatbot.meeting.airport_picket_yes', next: 'p_meeting_airport_picket_intro' },
        { labelKey: 'chatbot.meeting.airport_picket_no',  next: 'p_meeting_airport_no_picket_intro' },
      ],
    },

    /* BugHerd #28: 피켓 서비스 안내 intro — 호텔 패턴(Q1/Q2/Q3 선택)으로 통일.
       이전엔 중복 우려로 Q2/Q3 노드 제거했었으나 클라가 hotel 패턴 동일성 우선 요청. */
    p_meeting_airport_picket_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_picket_intro',
      choices: [
        { labelKey: 'chatbot.meeting.airport_picket_q1_label', next: 'p_meeting_airport_picket_q1' },
        { labelKey: 'chatbot.meeting.airport_picket_q2_label', next: 'p_meeting_airport_picket_q2' },
        { labelKey: 'chatbot.meeting.airport_picket_q3_label', next: 'p_meeting_airport_picket_q3' },
      ],
    },

    /* BugHerd #28: 주차장 직접 미팅 안내 intro — Q1/Q2/Q3 선택형 */
    p_meeting_airport_no_picket_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_no_picket_intro',
      choices: [
        { labelKey: 'chatbot.meeting.airport_no_picket_q1_label', next: 'p_meeting_airport_no_picket_q1' },
        { labelKey: 'chatbot.meeting.airport_no_picket_q2_label', next: 'p_meeting_airport_no_picket_q2' },
        { labelKey: 'chatbot.meeting.airport_no_picket_q3_label', next: 'p_meeting_airport_no_picket_q3' },
      ],
    },

    /* BugHerd #28: 답변 노드는 호텔 q1~q4 와 동일 — share_location 버튼만 노출.
       이전엔 다른 Q label / 상담원 연결까지 혼재돼 안내가 정리 안 된다는 보고. */
    p_meeting_airport_picket_q1: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_picket_q1',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_airport_picket_q2: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_picket_q2',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_airport_picket_q3: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_picket_q3',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_airport_no_picket_q1: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_no_picket_q1',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_airport_no_picket_q2: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_no_picket_q2',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_airport_no_picket_q3: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.airport_no_picket_q3',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_hotel: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.hotel_intro',
      choices: [
        { labelKey: 'chatbot.meeting.hotel_q1_label', next: 'p_meeting_hotel_q1' },
        { labelKey: 'chatbot.meeting.hotel_q2_label', next: 'p_meeting_hotel_q2' },
        { labelKey: 'chatbot.meeting.hotel_q3_label', next: 'p_meeting_hotel_q3' },
        { labelKey: 'chatbot.meeting.hotel_q4_label', next: 'p_meeting_hotel_q4' },
      ],
    },

    // BugHerd #29: 호텔 미팅 Q1~Q4 답변에서 "상담원 연결" 옵션 삭제(클라 명시).
    // 위치 알리기(share_location) 만 유지.
    p_meeting_hotel_q1: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.hotel_q1',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_hotel_q2: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.hotel_q2',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_hotel_q3: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.hotel_q3',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    p_meeting_hotel_q4: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.hotel_q4',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
      ],
    },

    /* ⚠️ 테크프리 가공 (deviation from client) — 2026-04-29
     * 클라이언트 원본(XMind): [📍 내 위치 알리기] 단일 버튼 (WhatsApp 라이브 위치 공유 1개)
     * 변경: [상담원 연결] 버튼 추가. 한국 사용자 중 WhatsApp 미사용 비율이 높아
     *       카카오톡·전화·이메일 등 다양한 채널로 기사 직통 연결을 가능하게 하기 위함.
     * 회신 요청 항목으로 클라이언트 컨펌 필요. (08 회신요청 참조)
     */
    p_meeting_lost: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.meeting.lost_msg',
      choices: [
        { labelKey: 'chatbot.meeting.share_location', next: 'p_loc_share' },
        { labelKey: 'chatbot.common.consultant',      next: 'p_consultant' },
      ],
    },

    p_loc_share: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.meeting.loc_share',
      choices: [
        { icon: 'whatsapp', labelKey: 'chatbot.meeting.whatsapp_open', action: ACTION_TYPES.LINK, href: CHANNELS.whatsapp },
      ],
    },

    /* ============================================================
     * Phase 2 — 💡 FAQ
     * ============================================================ */

    p_faq_intro: {
      // BugHerd #32: MENU(그리드) → CHOICE(직사각) 통일 + 아이콘 분별감 부여
      // (다른 카테고리와 동일 직사각 버튼 형태로 UX 일관성 회복)
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.faq.intro',
      choices: [
        { icon: 'place',    labelKey: 'chatbot.faq.lost_label',     next: 'p_meeting_lost' },
        { icon: 'vehicle',  labelKey: 'chatbot.faq.number_label',   next: 'p_vehicle_number' },
        { icon: 'search',   labelKey: 'chatbot.faq.lost_item',      next: 'p_faq_lost' },
        { icon: 'payment',  labelKey: 'chatbot.faq.payment',        next: 'p_pay_intro' },
        { icon: 'cancel',   labelKey: 'chatbot.res.cancel_policy',  next: 'p_res_cancel_policy' },
        { icon: 'coupon',   labelKey: 'chatbot.res.refund_policy',  next: 'p_res_refund_policy' },
        { icon: 'services', labelKey: 'chatbot.faq.service',        next: 'p_faq_service' },
        { icon: 'business', labelKey: 'chatbot.faq.business',       next: 'p_faq_business' },
      ],
    },

    p_faq_lost: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.lost_msg',
      choices: [{ labelKey: 'chatbot.common.consultant', next: 'p_consultant' }],
    },

    p_faq_service: {
      // BugHerd #32: MENU → CHOICE 통일 + 아이콘 분별감
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_msg',
      choices: [
        { icon: 'hospital', labelKey: 'chatbot.faq.service_hospital', next: 'p_faq_service_hospital' },
        { icon: 'globe',    labelKey: 'chatbot.faq.service_english',  next: 'p_faq_service_english' },
        { icon: 'pet',      labelKey: 'chatbot.vehicle.pet',          next: 'p_vehicle_pet' },
        { icon: 'clock',    labelKey: 'chatbot.faq.service_hours',    next: 'p_faq_service_hours' },
        { icon: 'alert',    labelKey: 'chatbot.res.noshow_label',     next: 'p_res_noshow' },
        { icon: 'cancel',   labelKey: 'chatbot.res.cancel',           next: 'p_res_intro' },
      ],
    },

    // BugHerd #26 (2026-05-07 코멘트): "FAQ → 서비스 관련 → 병원 동행 → 지금 예약하기"
    // 클릭 시 챗봇 내부 분기/INPUT 단계 거치지 않고 메인 병원 이동 페이지로 즉시 이동.
    p_faq_service_hospital: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_hospital_msg',
      choices: [
        { labelKey: 'chatbot.faq.service_hospital_price', next: 'p_faq_service_hospital_price' },
        { labelKey: 'chatbot.common.book_now',             action: ACTION_TYPES.NAVIGATE, to: '/transfer/hospital' },
      ],
    },

    p_faq_service_hospital_price: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_price_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now', action: ACTION_TYPES.NAVIGATE, to: '/transfer/hospital' },
      ],
    },

    // BugHerd #26 일관성: hospital 흐름과 동일하게 "지금 예약하기" 진입점 제공.
    // 영어 가능 기사는 6개 서비스 모두 적용되는 옵션이라 p_booking_private(6개 NAVIGATE 카드)로
    // 안내해 사용자가 서비스 선택 후 즉시 페이지 이동.
    p_faq_service_english: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_english_msg',
      choices: [
        { labelKey: 'chatbot.faq.service_english_price', next: 'p_faq_service_english_price' },
        { labelKey: 'chatbot.common.book_now',            next: 'p_booking_private' },
      ],
    },

    p_faq_service_english_price: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_price_msg',
      choices: [
        { labelKey: 'chatbot.common.book_now', next: 'p_booking_private' },
      ],
    },

    p_faq_service_hours: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.service_hours_msg',
      choices: [
        { icon: 'edit',     labelKey: 'chatbot.common.inquiry_board', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry },
        { icon: 'whatsapp', labelKey: 'chatbot.common.whatsapp_24h',   action: ACTION_TYPES.LINK, href: CHANNELS.whatsapp },
      ],
    },

    p_faq_business: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.faq.business_msg',
      choices: [
        { icon: 'edit', labelKey: 'chatbot.faq.business_link', action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry },
      ],
    },

    /* ============================================================
     * Phase 2 — 💳 결제 (FAQ → 결제 진입)
     * ============================================================ */

    p_pay_intro: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.intro',
      choices: [
        { labelKey: 'chatbot.pay.methods',         next: 'p_pay_methods' },
        { labelKey: 'chatbot.pay.receipt',         next: 'p_pay_receipt' },
        { labelKey: 'chatbot.pay.va_check',        next: 'p_pay_va_check' },
        { labelKey: 'chatbot.pay.error',           next: 'p_pay_error' },
        { labelKey: 'chatbot.pay.refund_pending',  next: 'p_pay_refund_pending' },
      ],
    },

    p_pay_methods: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.methods_msg',
      choices: [{ labelKey: 'chatbot.common.consultant', next: 'p_consultant' }],
    },

    p_pay_receipt: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.receipt_msg',
      choices: [{ labelKey: 'chatbot.common.consultant', next: 'p_consultant' }],
    },

    p_pay_va_check: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.va_check_msg',
      choices: [
        { icon: 'reservation', labelKey: 'chatbot.pay.go_to_reservation', action: ACTION_TYPES.NAVIGATE, to: '/reservation' },
        { icon: 'headset',     labelKey: 'chatbot.common.consultant',     next: 'p_consultant' },
      ],
    },

    p_pay_error: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.error_msg',
      choices: [
        { labelKey: 'chatbot.common.consultant', next: 'p_consultant' },
        { labelKey: 'chatbot.pay.retry_payment', next: 'p_main_menu' },
      ],
    },

    p_pay_refund_pending: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.pay.refund_pending_msg',
      choices: [{ labelKey: 'chatbot.common.consultant', next: 'p_consultant' }],
    },

    /* ============================================================
     * Phase 2 — 📞 상담원 연결
     * ============================================================ */

    p_consultant: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.consultant.intro',
      // 운영시간 외 분기는 D3에서 useBusinessHours 활용해 messageKey 동적 변경
      choices: [
        { icon: 'kakao',    labelKey: 'chatbot.consultant.kakao',    action: ACTION_TYPES.LINK, href: CHANNELS.kakao },
        { icon: 'whatsapp', labelKey: 'chatbot.consultant.whatsapp', action: ACTION_TYPES.LINK, href: CHANNELS.whatsapp },
        { icon: 'line',     labelKey: 'chatbot.consultant.line',     action: ACTION_TYPES.LINK, href: CHANNELS.line },
        { icon: 'email',    labelKey: 'chatbot.consultant.email',    action: ACTION_TYPES.LINK, href: `mailto:${CHANNELS.email}` },
        { icon: 'phone',    labelKey: 'chatbot.consultant.phone',    action: ACTION_TYPES.TEL,  number: CHANNELS.phone.replace(/-/g, '') },
        { icon: 'edit',     labelKey: 'chatbot.consultant.inquiry',  action: ACTION_TYPES.NAVIGATE, to: CHANNELS.inquiry },
      ],
    },

    /* ============================================================
     * Phase 3 — 마무리
     * ============================================================ */

    p_feedback: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.feedback.prompt',
      choices: [
        { labelKey: 'chatbot.feedback.yes', next: 'p_feedback_yes' },
        { labelKey: 'chatbot.feedback.no',  next: 'p_feedback_no' },
      ],
    },

    p_feedback_yes: {
      type: NODE_TYPES.BOT,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.feedback.yes_msg',
      next: 'p_main_menu',  // 자동 메인 메뉴 복귀
    },

    p_feedback_no: {
      type: NODE_TYPES.BOT,
      source: SOURCES.TECH,
      messageKey: 'chatbot.feedback.no_msg',
      next: 'p_consultant',  // 자동 상담원 (옵션 C)
    },

    p_idle_prompt: {
      type: NODE_TYPES.PROMPT,
      source: SOURCES.MIXED,
      messageKey: 'chatbot.idle.prompt',
      autoTimeoutMs: 30 * 1000,
      buttons: [
        { labelKey: 'chatbot.idle.yes', next: 'p_main_menu' },
        { labelKey: 'chatbot.idle.no',  next: 'p_close_normal' },
      ],
    },

    p_exit_guard_close: {
      type: NODE_TYPES.MODAL,
      source: SOURCES.XMIND,
      messageKey: 'chatbot.exit_guard.close',
      buttons: [
        { labelKey: 'chatbot.exit_guard.yes',        next: 'p_close_normal' },
        { labelKey: 'chatbot.common.consultant',     next: 'p_consultant' },
        { labelKey: 'chatbot.exit_guard.no',         action: 'cancel' },
      ],
    },

    p_exit_guard_payment: {
      type: NODE_TYPES.MODAL,
      source: SOURCES.TECH,
      messageKey: 'chatbot.exit_guard.payment',
      buttons: [
        { labelKey: 'chatbot.exit_guard.payment_continue', action: 'cancel' },
        { labelKey: 'chatbot.exit_guard.payment_chatbot',  next: 'p_main_menu' },
      ],
    },

    p_close_normal: {
      type: NODE_TYPES.BOT,
      source: SOURCES.TECH,
      messageKey: 'chatbot.close.normal',
      // next 없음 = 종결 (Panel 자동 unmount는 D3에서)
    },

    p_close_after_hours: {
      type: NODE_TYPES.CHOICE,
      source: SOURCES.TECH,
      messageKey: 'chatbot.close.after_hours',
      choices: [
        { icon: 'whatsapp', labelKey: 'chatbot.consultant.whatsapp_link_24h', action: ACTION_TYPES.LINK, href: CHANNELS.whatsapp },
      ],
    },
  },
};

export default CHATBOT_SCENARIOS;
